import { createSlice } from '@reduxjs/toolkit';
import {
    PlanningQuestion,
    PlanningSessionStatusEnum
} from 'src/types/planning';
import {
    startPlanningSession,
    fetchPlanningSession,
    submitAnswers,
    submitFeedback,
    editPlan,
    finalizePlan,
    offloadPlanningSession,
    retryPlanningSession,
    SLICE_NAME
} from './service';

interface PlanningState {
    sessionId: string | null;
    status: PlanningSessionStatusEnum | null;
    questions: PlanningQuestion[] | null;
    plan: string | null;
    turnCount: number;
    questId: string | null;
    clarityScore: number | null;
    isLoading: boolean;
    isOffloading: boolean;
    error: string | null;
}

const initialState: PlanningState = {
    sessionId: null,
    status: null,
    questions: null,
    plan: null,
    turnCount: 0,
    questId: null,
    clarityScore: null,
    isLoading: false,
    isOffloading: false,
    error: null
};

export const planningSlice = createSlice({
    name: SLICE_NAME,
    initialState,
    reducers: {
        resetPlanning: () => initialState
    },
    extraReducers: (builder) => {
        builder
            .addCase(startPlanningSession.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(startPlanningSession.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                if (payload.questId && !payload.sessionId) {
                    state.questId = payload.questId;
                    state.status = PlanningSessionStatusEnum.FINALIZED;
                } else {
                    state.sessionId = payload.sessionId ?? null;
                    state.status = PlanningSessionStatusEnum.PROCESSING;
                }
            })
            .addCase(startPlanningSession.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            .addCase(fetchPlanningSession.fulfilled, (state, { payload }) => {
                state.sessionId = payload.sessionId;
                state.status = payload.status;
                state.questions = payload.questions;
                state.plan = payload.plan;
                state.turnCount = payload.turnCount;
                state.questId = payload.questId;
                state.clarityScore = payload.clarityScore;
            })
            .addCase(submitAnswers.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.status = PlanningSessionStatusEnum.PROCESSING;
                state.questions = null;
            })
            .addCase(submitAnswers.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(submitAnswers.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            .addCase(submitFeedback.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.status = PlanningSessionStatusEnum.PROCESSING;
            })
            .addCase(submitFeedback.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(submitFeedback.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            .addCase(editPlan.fulfilled, (state, { payload }) => {
                state.plan = payload.plan;
            })
            .addCase(finalizePlan.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(finalizePlan.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.questId = payload.questId;
                state.status = PlanningSessionStatusEnum.FINALIZED;
            })
            .addCase(finalizePlan.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            .addCase(retryPlanningSession.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.status = PlanningSessionStatusEnum.PROCESSING;
            })
            .addCase(retryPlanningSession.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(retryPlanningSession.rejected, (state, action) => {
                state.isLoading = false;
                state.status = PlanningSessionStatusEnum.FAILED;
                state.error = action.payload as string;
            })
            .addCase(offloadPlanningSession.pending, (state) => {
                state.isOffloading = true;
                state.error = null;
            })
            .addCase(offloadPlanningSession.fulfilled, (state) => {
                state.isOffloading = false;
                // Backend parks the session in PROCESSING; reflect it
                // so any local checks see the same shape until the
                // navigation away from this page unmounts the slice.
                state.status = PlanningSessionStatusEnum.PROCESSING;
            })
            .addCase(offloadPlanningSession.rejected, (state, action) => {
                state.isOffloading = false;
                state.error = action.payload as string;
            });
    }
});

export const { resetPlanning } = planningSlice.actions;
export default planningSlice.reducer;
